import { Node } from "@tiptap/core";
import type { Node as NodeSchema } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { ReactNodeViewProps, ReactNodeViewRenderer } from "@tiptap/react";

import { getRelativeTime } from "@utils/date.util";
import type { UploaderItem, UploaderOptions } from "../../extensions/Uploader";
import { findCustomNodeByAttribute } from "../../extensions/utils";
import { watchNodeDeletion } from "../utils";

import type { DocumentAttributes, DocumentOptions, DocumentStorage } from "./types";

export const Document = Node.create<DocumentOptions, DocumentStorage>({
  name: "document",

  group: "block",

  inline: false,

  draggable: true,

  selectable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      component: () => null,
      onUpload: () => Promise.resolve([]),
      onDelete: () => Promise.resolve(),
    };
  },

  addStorage() {
    return {
      uploader: {
        match: (_, data) => {
          return data.type === "application/pdf";
        },
        apply(editor, data) {
          editor.commands.uploadDocuments([data.file]);
        },
      } as UploaderItem,
      uploads: new Map<string, AbortController>(),
    };
  },

  renderHTML({ node }) {
    const attrs = node.attrs as DocumentAttributes;

    return [
      "div",
      {
        class:
          "not-prose mb-4 grid w-full items-center gap-3 rounded-lg border border-stroke-primary px-4 py-3 text-left",
      },
      [
        "div",
        {
          class: "relative -mx-4 -mt-3 h-56 flex-none overflow-hidden rounded-t-lg bg-slate-100",
        },
        [
          "img",
          {
            src: attrs.preview_url,
            class: "absolute inset-0 object-cover",
          },
        ],
      ],
      [
        "div",
        { class: "flex items-center gap-4" },
        [
          "div",
          { class: "flex-none" },
          [
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              width: "24",
              height: "24",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              class: "text-red-500",
            },
            [
              "path",
              {
                d: "M12 2l.117 .007a1 1 0 0 1 .876 .876l.007 .117v4l.005 .15a2 2 0 0 0 1.838 1.844l.157 .006h4l.117 .007a1 1 0 0 1 .876 .876l.007 .117v9a3 3 0 0 1 -2.824 2.995l-.176 .005h-10a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-14a3 3 0 0 1 2.824 -2.995l.176 -.005h5z",
                fill: "currentColor",
                "stroke-width": "0",
              },
            ],
            [
              "path",
              {
                d: "M19 7h-4l-.001 -4.001z",
                fill: "currentColor",
                "stroke-width": "0",
              },
            ],
          ],
        ],
        [
          "div",
          { class: "pointer-events-none flex flex-1 flex-col truncate" },
          [
            "strong",
            {
              class: "truncate text-sm font-medium",
              title: attrs.file_name,
            },
            attrs.file_name,
          ],
          [
            "p",
            { class: "truncate text-xs text-content-secondary" },
            `Uploaded by ${attrs.user_full_name} on ${getRelativeTime(attrs.created)}`,
          ],
        ],
      ],
    ];
  },

  renderText({ node }) {
    const attrs = node.attrs as DocumentAttributes;
    const date = new Date(attrs.created || "");
    const file_name = `${attrs.file_name} - Uploaded by ${attrs.user_full_name} on ${getRelativeTime(date)}`;

    return `[${file_name}](${attrs.file})`;
  },

  addAttributes() {
    return {
      id: {
        default: null,
      },
      file: {
        default: null,
      },
      file_name: {
        default: null,
      },
      created: {
        default: null,
      },
      user_id: {
        default: null,
      },
      user_full_name: {
        default: null,
      },
      user_avatar_url: {
        default: null,
      },
      uploading: {
        default: false,
      },
      show_preview: {
        default: true,
      },
      preview_url: {
        default: null,
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      this.options.component as React.ComponentType<ReactNodeViewProps<HTMLElement>>
    );
  },

  addCommands() {
    const findDocumentNodeById = (id: string) => (doc: NodeSchema) => {
      const node = findCustomNodeByAttribute(doc, this.type, "id", id);
      if (!node) {
        console.warn(`Document node with ID ${id} not found`);
        return null;
      }
      return node;
    };

    const deleteDocumentNode = (node: { node: NodeSchema; pos: number }) => {
      this.editor.commands.deleteRange({
        from: node.pos,
        to: node.pos + node.node.nodeSize,
      });
    };

    return {
      insertDocument:
        (options) =>
        ({ tr, state, view }) => {
          const { selection, schema } = state;
          const { from, empty } = selection;
          const documentNode = schema.nodes[this.name].create(options);

          if (!empty) {
            tr.deleteSelection();
          }

          const $from = tr.doc.resolve(from);
          const isAtEndOfParagraph =
            $from.parent.type === schema.nodes.paragraph &&
            $from.parentOffset === $from.parent.content.size;

          // Ensure the document node is inserted as a separate block if at the end of a paragraph
          if (isAtEndOfParagraph) {
            tr.split(from);
            const afterSplitPos = from + 1;
            tr.insert(afterSplitPos, documentNode);
            const positionAfterDocument = afterSplitPos + documentNode.nodeSize;
            tr.setSelection(TextSelection.create(tr.doc, positionAfterDocument));

            if (positionAfterDocument >= tr.doc.content.size) {
              const paragraphNode = schema.nodes.paragraph.create();
              tr.insert(positionAfterDocument, paragraphNode);
              tr.setSelection(TextSelection.create(tr.doc, positionAfterDocument + 1));
            }
          } else {
            tr.insert(from, documentNode);

            const positionAfterDocument = from + documentNode.nodeSize;

            // Add paragraph at the end if necessary
            if (positionAfterDocument >= tr.doc.content.size) {
              const paragraphNode = schema.nodes.paragraph.create();
              tr.insert(positionAfterDocument, paragraphNode);
              tr.setSelection(TextSelection.create(tr.doc, positionAfterDocument + 1));
            } else {
              // Move caret after inserted node
              tr.setSelection(TextSelection.create(tr.doc, positionAfterDocument));
            }
          }

          view.dispatch(tr);

          return true;
        },

      updateDocumentById:
        (id, options) =>
        ({ tr }) => {
          const node = findDocumentNodeById(id)(tr.doc);
          if (!node) return false;

          tr.setNodeMarkup(node.pos, undefined, {
            ...node.node.attrs,
            ...options,
          });

          return true;
        },
      loadDocument: () => () => {
        const uploader = this.editor.storage.uploader as UploaderOptions;
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "application/pdf";
        fileInput.multiple = false;

        fileInput.addEventListener("change", () => {
          if (!fileInput.files) return;

          uploader
            .upload(fileInput.files)
            .then((items) => {
              this.editor.commands.uploadDocuments(items.map((item) => item.file));
            })
            .catch((error) => {
              console.error("Failed to upload document", error);
            });
        });

        fileInput.click();

        return true;
      },
      uploadDocuments:
        (files) =>
        ({ commands }) => {
          const hash = files.map((file) => file.name).join("");
          const controller = new AbortController();

          this.storage.uploads.set(hash, controller);

          files.forEach((item) => {
            commands.insertDocument({
              id: item.name,
              file: "",
              file_name: item.name,
              created: new Date().toISOString(),
              user_id: "",
              user_full_name: "",
              preview_url: "",
              user_avatar_url: "",
              uploading: true,
              show_preview: true,
            });
          });

          this.options
            .onUpload(files, controller.signal)
            .then((items) => {
              items.forEach((item, index) => {
                this.editor.commands.updateDocumentById(files[index].name, {
                  id: item.id,
                  file: item.file,
                  file_name: item.file_name,
                  created: item.created,
                  preview_url: item.file_preview,
                  user_avatar_url: item.user?.avatar_url,
                  user_full_name: item.user?.full_name,
                  user_id: item.user?.id,
                  uploading: false,
                });
              });

              return items;
            })
            .catch((error) => {
              console.error("Failed to upload document", error);

              files.forEach((file) => {
                const node = findCustomNodeByAttribute(
                  this.editor.view.state.doc,
                  this.type,
                  "id",
                  file.name
                );

                if (!node) return;

                deleteDocumentNode(node);
              });
            });

          return true;
        },
      cancelDocumentUpload: (id) => {
        return ({ tr }) => {
          const node = findDocumentNodeById(id)(tr.doc);
          if (!node) return false;

          const hash = node.node.attrs.file_name;
          const controller = this.storage.uploads.get(hash as string);

          if (!controller) {
            console.warn(`Upload controller for document ${id} not found`);
          } else {
            controller.abort();
          }

          deleteDocumentNode(node);

          return true;
        };
      },
      downloadDocumentById:
        (id) =>
        ({ tr }) => {
          const node = findDocumentNodeById(id)(tr.doc);
          if (!node) return false;

          const a = document.createElement("a");
          a.href = node.node.attrs.file as string;
          a.download = String(node.node.attrs.file_name);
          a.target = "_blank";
          a.click();

          return true;
        },
      removeDocumentById:
        (id) =>
        ({ tr }) => {
          const node = findDocumentNodeById(id)(tr.doc);
          if (!node) return false;

          deleteDocumentNode(node);

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      watchNodeDeletion({
        onDelete: (node) => this.options.onDelete(node.attrs as DocumentAttributes),
        isNodeType: (node) => node.type === this.type && node.attrs.id && !node.attrs.uploading,
        isEqual: (a, b) => a.attrs.id === b.attrs.id,
      }),
    ];
  },
});
